import React, { useEffect, useState, useCallback } from 'react';
import { useFeature } from '../hooks/useFeatures';
import { useScreenShare } from '../context/ScreenShareContext';
import './EnforcementOverlay.css';

/**
 * EnforcementOverlay - Renders time-limit enforcement state on the screen share video
 *
 * Shows:
 * - WARNING state: Yellow pulsing border with remaining time
 * - CRITICAL state: Red pulsing border with urgent countdown
 * - LOCKOUT state: Red overlay with "BLOCKED" indicator
 */
const EnforcementOverlay = () => {
  // useFeature polls until the feature registers — handles the cold-load case
  // where this overlay mounts before time-limit has finished registering.
  const timeLimitFeature = useFeature('time-limit');
  const { getDisplayBounds, nativeResolution, screenShareVideoRef } = useScreenShare();
  const [enforcementState, setEnforcementState] = useState({ activeOverlays: [] });

  // Subscribe to enforcement state updates from time-limit feature
  useEffect(() => {
    if (!timeLimitFeature) return undefined;

    // Subscribe to enforcement state updates
    const unsubscribe = timeLimitFeature.onEnforcementStateUpdate?.((state) => {
      setEnforcementState(state || { activeOverlays: [] });
    });

    // Also request initial state
    timeLimitFeature.getEnforcementState?.().then((state) => {
      if (state?.success) {
        setEnforcementState(state || { activeOverlays: [] });
      }
    }).catch(err => console.warn('[EnforcementOverlay] Failed to fetch enforcement state:', err.message));

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [timeLimitFeature]);

  // Format remaining time as MM:SS
  const formatTime = useCallback((seconds) => {
    if (seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Get the appropriate CSS class for a state
  const getStateClass = useCallback((state) => {
    switch (state) {
      case 'WARNING':
        return 'state-warning';
      case 'CRITICAL':
        return 'state-critical';
      case 'LOCKOUT':
        return 'state-lockout';
      default:
        return '';
    }
  }, []);

  // Don't render if no active overlays or video not ready
  if (!enforcementState.activeOverlays?.length || !nativeResolution.width) {
    return null;
  }

  return (
    <div className="enforcement-overlay-container">
      {enforcementState.activeOverlays.map((overlay, index) => {
        // Skip if no bounds
        if (!overlay.bounds) return null;

        // Convert screen bounds to display coordinates
        const displayBounds = getDisplayBounds({
          x: overlay.bounds.x,
          y: overlay.bounds.y,
          width: overlay.bounds.width,
          height: overlay.bounds.height
        });

        if (!displayBounds) return null;

        const stateClass = getStateClass(overlay.state);
        const isLockout = overlay.state === 'LOCKOUT';

        return (
          <div
            key={`${overlay.appName}-${index}`}
            className={`enforcement-overlay ${stateClass}`}
            style={{
              position: 'absolute',
              left: displayBounds.x,
              top: displayBounds.y,
              width: displayBounds.width,
              height: displayBounds.height,
              pointerEvents: 'none',
              zIndex: isLockout ? 200 : 150
            }}
          >
            {/* Border overlay */}
            <div className="overlay-border" />

            {/* Label at top */}
            <div className="overlay-label">
              <span className="app-name">{overlay.appName}</span>
              {!isLockout && overlay.remaining > 0 && (
                <span className="remaining-time">
                  {formatTime(overlay.remaining)}
                </span>
              )}
              {isLockout && (
                <span className="blocked-indicator">BLOCKED</span>
              )}
              {overlay.isRemoteBlocked && (
                <span className="remote-blocked-badge">REMOTE</span>
              )}
            </div>

            {/* Lockout fill overlay */}
            {isLockout && (
              <div className="lockout-fill">
                <div className="lockout-icon">
                  <i className="simple-icon-lock" />
                </div>
              </div>
            )}

            {/* Critical countdown in center */}
            {overlay.state === 'CRITICAL' && overlay.remaining <= 10 && (
              <div className="critical-countdown">
                {Math.ceil(overlay.remaining)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EnforcementOverlay;
